import styled from 'styled-components';
import { useState } from 'react';
import UserService from '../../service/User';

interface ConfirmModalProps {
  setShowProfileUpdateModal: (value: boolean) => void;
  getInfo: () => void;
}

const ProfileUpdateModal = ({
  setShowProfileUpdateModal,
  getInfo,
}: ConfirmModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];

      // Check file extension
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      if (fileExtension && !['jpeg', 'jpg', 'png'].includes(fileExtension)) {
        alert('지원되는 파일 형식은 jpeg, jpg, png 만 가능합니다.');
        setFile(null); // Reset the file input
        setFileName(''); // Reset the file name
        setImagePreview(null); // Reset image preview
        return; // Exit function if invalid file type
      }

      setFile(selectedFile);
      setFileName(selectedFile.name); // Update file name

      // Create a preview of the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string); // Set the image preview URL
      };
      reader.readAsDataURL(selectedFile); // Read the file as a data URL
    }
  };

  const updateProfile = async () => {
    if (!file) {
      console.warn('파일이 선택되지 않았습니다.');
      return;
    }

    const formData = new FormData();
    formData.append('img', file);

    try {
      const res = await UserService.updateProfile(formData);
      alert('프로필이 변경되었습니다.');
      getInfo();
      setShowProfileUpdateModal(false);
    } catch (error) {
      console.error('updateProfile 함수에서 에러 발생:', error);
    }
  };

  return (
    <Container>
      <Inner>
        <TextBox>
          <Title>변경할 프로필 이미지를 선택해 주세요.</Title>
        </TextBox>
        <InputBox>
          <FileName>{fileName}</FileName>
          <CustomButton
            onClick={() => document.getElementById('fileInput')?.click()}
          >
            불러오기
          </CustomButton>
          <Input
            id="fileInput"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </InputBox>
        {imagePreview && (
          <ImagePreview>
            <img src={imagePreview} alt="Selected file preview" />
          </ImagePreview>
        )}
        <ButtonBox>
          <CancelButton onClick={() => setShowProfileUpdateModal(false)}>
            취소
          </CancelButton>
          <ApplyButton disabled={file === null} onClick={updateProfile}>
            변경
          </ApplyButton>
        </ButtonBox>
      </Inner>
    </Container>
  );
};

export default ProfileUpdateModal;

const Container = styled.div`
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Inner = styled.div`
  width: 90%;
  padding: 20px 16px;
  background-color: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-weight: 700;
  color: #171e26;
`;

const TextBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 500;
  color: #616161;
`;
const Title = styled.span`
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-weight: 700;
  color: #222;
`;

const ApplyButton = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 8px;
  background: ${({ disabled }) =>
    disabled ? 'rgba(112, 115, 124, 0.08)' : '#008AFF'};
  color: ${({ disabled }) => (disabled ? 'rgba(55, 56, 60, 0.16)' : '#fff')};
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: none;
`;
const CancelButton = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 8px;
  background: rgba(112, 115, 124, 0.08);
  color: #616161;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: none;
`;

const Input = styled.input`
  border: none;
  background: none;
  flex: 1;
  outline: none;
`;

const ButtonBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const CustomButton = styled.button`
  width: 111px;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #008aff;
  color: #008aff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
`;

const InputBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FileName = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 8px;
  background: #f5f7fa;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-weight: 500;
  color: #616161;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 200px;
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
`;
